import React from 'react';
import firebase from '../../config/firebaseConfig';
import { useState, useEffect } from 'react';
import { SUPER_ADMIN_UID } from '../../config/adminConfig';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Spinner, Alert, Card, Form, InputGroup, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';
import SidebarAdmin from './SidebarAdmin';
import { DEPARTEMENTS_FRANCAIS } from '../../config/departements';
import moment from 'moment';
import 'moment/locale/fr';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const navigate = useNavigate();

  // États pour la modification d'un utilisateur
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editZipCode, setEditZipCode] = useState('');
  const [editEmail, setEditEmail] = useState('');

  // États pour la suppression (Nouvelle Modale)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // 🌟 ÉTATS POUR LE TOAST
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Fonction utilitaire pour déclencher le Toast
  const triggerToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Récupération initiale des données
  const fetchUsers = async () => {
    try {
      const snapshot = await firebase.firestore().collection('users').get();
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        // 1. On va chercher son rôle en direct sur Firestore pour ce compte précis
        const docSnap = await firebase.firestore().collection('users').doc(user.uid).get();
        const userRole = docSnap.exists ? docSnap.data().role : 'membre';

        // 🎯 LE NOUVEAU VERROU : Accès accordé si c'est TOI (Super-Admin) OU si son rôle Firestore est 'admin'
        if (user.uid === SUPER_ADMIN_UID || userRole === 'admin') {
          setIsAdmin(true);
          await fetchUsers();
        } else {
          // 🛑 S'il est repassé membre, il est éjecté instantanément à l'accueil !
          navigate('/');
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const term = searchTerm.toLowerCase().trim();
    if (term === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => {
        const name = (user.name || '').toLowerCase();
        const firstName = (user.firstName || '').toLowerCase();
        const city = (user.city || '').toLowerCase();

        // Vérifie si le terme recherché est présent dans le nom, le prénom ou la ville
        return name.includes(term) || firstName.includes(term) || city.includes(term);
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const changerStatutUtilisateur = async (userId, nouveauStatut) => {
    try {
      await firebase.firestore().collection('users').doc(userId).update({
        role: nouveauStatut // 🚀 Envoie "admin" ou "membre" à Firebase
      });
      triggerToast("Statut de l'utilisateur mis à jour !", "success");
    } catch (err) {
      triggerToast("Impossible de modifier le statut", "danger");
    }
  };

  // Ouvrir le modal de modification et pré-remplir les champs
  const handleOpenEdit = (user) => {

    setSelectedUser(user);
    setEditName(user.name || '');
    setEditFirstName(user.firstName || '');
    setEditCity(user.city || '');
    setEditZipCode(user.zipCode || '');
    setEditEmail(user.email || '');
    setShowEditModal(true);
  };

  // Enregistrer les modifications dans Firestore
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await firebase.firestore().collection('users').doc(selectedUser.id).update({
        name: editName,
        firstName: editFirstName,
        city: editCity,
        zipCode: editZipCode,
        email: editEmail
      });

      setShowEditModal(false);

      await fetchUsers(); // Rafraîchit la liste
      triggerToast("Profil mis à jour avec succès !", "success"); // 🌟 Déclenchement Toast
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
      alert("Impossible de modifier l'utilisateur.");
    }
  };

  // Ouvrir la modale de confirmation de suppression
  const handleOpenDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Confirmer et exécuter la suppression dans Firestore
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await firebase.firestore().collection('users').doc(userToDelete.id).delete();
      setShowDeleteModal(false);
      setUserToDelete(null);
      await fetchUsers(); // Rafraîchit la liste
      triggerToast("Utilisateur supprimé définitivement.", "success"); // 🌟 Déclenchement Toast
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      alert("Impossible de supprimer l'utilisateur.");
    }
  };

  const renderRegistrationDate = (createdAt) => {
    if (!createdAt) return "Non renseignée";

    if (createdAt.toDate) {
      return moment(createdAt.toDate()).locale('fr').format('DD/MM/YYYY [à] HH:mm');
    }

    return moment(createdAt).locale('fr').format('DD/MM/YYYY [à] HH:mm');
  };


  // 💡 Détecte si les champs sont restés identiques aux données d'origine
  const isUnchanged = selectedUser &&
    editName === (selectedUser.name || '') &&
    editFirstName === (selectedUser.firstName || '') &&
    editCity === (selectedUser.city || '') &&
    editZipCode === (selectedUser.zipCode || '') &&
    editEmail === (selectedUser.email || '');

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  // Sécurité renforcée pendant le rendu
  if (!isAdmin) return null;

  return (
    <Container fluid className="py-4 min-vh-100 text-light">
      <div className="d-flex flex-column flex-md-row">
        <SidebarAdmin />

        {/* 🚀 2. LE CONTENEUR WRAPPER QUI CORRIGE L'ALIGNEMENT DES PAGES */}
        <div className="admin-main-wrapper w-100" style={{ marginBottom: '80px' }}>
        
          <Container className="py-2 bg-profil min-vh-100">

            <Card className="shadow-sm p-2 p-sm-4 border-0">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center mb-4 responsive-card-header-admin">
                <Card.Title className="mb-4 fw-semibold text-danger">🛑 Espace Administrateur — Liste des inscrits</Card.Title>

                {/* 🔍 BARRE DE RECHERCHE DYNAMIQUE */}
                <Form.Group className="w-100 style-search-container" style={{ maxWidth: '380px' }}>
                  <InputGroup className="p-2">
                    <InputGroup.Text className="bg-white border-end-0">
                      <i className="material-icons text-muted" style={{ fontSize: '25px' }}>search</i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher par nom, prénom ou ville..."
                      className="border-start-0"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </div>

              {/* --- SECTION DES UTILISATEURS --- */}
              {filteredUsers.length === 0 ? (
                <Alert variant="warning">
                  {users.length === 0 ? "Aucun inscrit trouvé dans la base de données." : "Aucun utilisateur ne correspond à votre recherche."}
                </Alert>
              ) : (
                  <Table striped bordered hover responsive fill="true" className="responsive-table-admin" style={{ cursor: 'pointer' }}>
                    <thead className="table-dark">
                      <tr>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Ville</th>
                        <th>Code Postal</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Date d'inscription</th>
                        <th className="text-center" style={{ width: '160px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.name || <em className="text-muted">Non renseigné</em>}</td>
                          <td>{user.firstName || <em className="text-muted">Non renseigné</em>}</td>
                          <td>{user.city || <em className="text-muted">Non renseigné</em>}</td>
                          <td className="fw-semibold">
                            {user.zipCode || <em className="text-muted">Non renseigné</em>}
                          </td>
                          <td>{user.email || <em className="text-muted">Non renseigné</em>}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              defaultValue={user.role || 'membre'}
                              onChange={(e) => changerStatutUtilisateur(user.id, e.target.value)}
                              className="bg-dark text-light border-secondary w-auto"
                            >
                              <option value="membre" className="text-light"><span>👤</span> Membre</option>
                              <option value="admin" className="text-warning"><span>👑</span> Administrateur</option>
                            </Form.Select>
                          </td>
                          <td>{renderRegistrationDate(user.createdAt)}</td>
                          <td>
                            <div className="admin-action-btn-group text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleOpenEdit(user)}
                                title="Modifier"
                              >
                                <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>edit</i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleOpenDelete(user)}
                                title="Supprimer"
                              >
                                <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>delete</i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
            </Card>

            {/* --- SECTION DES DÉPARTEMENTS FRANÇAIS --- */}
            <div className="card shadow-sm border-0 mt-5 p-2 p-sm-4 rounded-3">
              <div className="d-flex justify-content-between align-items-center mb-3 responsive-card-header">
                <h4 className="card-title fs-5 mb-0 text-dark d-flex align-items-center gap-2">
                  <i className="bi bi-map-fill text-primary"></i> Répartition par département français
                </h4>
                <span className="badge bg-secondary p-2">Total : 101 départements</span>
              </div>

              <div className="table-responsive" style={{ maxHeight: '660px', overflowY: 'auto' }}>
                <table className="table table-hover align-middle mb-0 responsive-table">
                  <thead className="table-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '15%' }}>N°</th>
                      <th style={{ width: '40%' }}>Département</th>
                      <th style={{ width: '30%' }}>Région</th>
                      <th style={{ width: '15%' }} className="text-center">Membres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPARTEMENTS_FRANCAIS.map((dept) => {

                      // 🚀 Calcul automatique : extrait les 2 premiers chiffres du zipCode (ex: "34190" -> "34")
                      const countInscrits = users?.filter(u => {
                        if (!u.zipCode) return false;

                        // On prend les 2 premiers caractères du code postal
                        const deptCodeUser = u.zipCode.toString().trim().substring(0, 2);

                        return deptCodeUser === dept.code;
                      }).length || 0;

                      return (
                        <tr key={dept.code}>
                          <td>
                            <span className="badge bg-light text-dark border fw-semibold px-3 py-2">
                              {dept.code}
                            </span>
                          </td>
                          <td className="fw-semibold text-secondary">{dept.nom}</td>
                          <td className="text-muted small">{dept.region}</td>
                          <td className="text-center">
                            <span className={`responsive-flag badge rounded-pill px-3 ${countInscrits > 0 ? 'bg-primary' : 'bg-light text-muted border'}`}>
                              {countInscrits}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered contentClassName="bg-dark text-light border-secondary" backdrop="static">
              <Modal.Header closeButton closeVariant="white" className="border-secondary">
                <Modal.Title className="text-warning fw-semibold">Modifier le profil</Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleSaveChanges}>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '20px' }}>
                  <Form.Group className="mb-1" controlId="formName">
                    <Form.Label className="small fw-semibold text-secondary">Nom</Form.Label>
                    <Form.Control
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="bg-secondary text-white border-0"
                    />
                  </Form.Group>

                  <Form.Group className="mb-1" controlId="formFirstName">
                    <Form.Label className="small fw-semibold text-secondary">Prénom</Form.Label>
                    <Form.Control
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      className="bg-secondary text-white border-0"
                    />
                  </Form.Group>

                  <Form.Group className="mb-1" controlId="formCity">
                    <Form.Label className="small fw-semibold text-secondary">Ville</Form.Label>
                    <Form.Control
                      type="text"
                      value={editCity}
                      onChange={(e) => setEditCity(e.target.value)}
                      className="bg-secondary text-white border-0"
                    />
                  </Form.Group>

                  <Form.Group className="mb-1" controlId="formZipCode">
                    <Form.Label className="small fw-semibold text-secondary">Code Postal</Form.Label>
                    <Form.Control
                      type="text"
                      maxLength={5}
                      value={editZipCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9abAB]/g, '');
                        setEditZipCode(value);
                      }}
                      className="bg-secondary text-white border-0"
                    />
                  </Form.Group>

                  <Form.Group className="mb-1" controlId="formEmail">
                    <Form.Label className="small fw-semibold text-secondary">Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="bg-secondary text-white border-0"
                    />
                  </Form.Group>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-end border-secondary border-1 pt-3">
                  <Button variant="secondary" className="px-3 fw-semibold" onClick={() => setShowEditModal(false)}>
                    Annuler
                  </Button>
                  <Button variant="success" className="px-3 fw-semibold" type="submit" disabled={isUnchanged}
                    style={{ opacity: isUnchanged ? 0.5 : 1, cursor: isUnchanged ? 'not-allowed' : 'pointer' }}
                  >
                    Editer le profil
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered contentClassName="bg-dark text-white border-danger border-2">
              <Modal.Header closeButton closeVariant="white" className="border-secondary">
                <Modal.Title className="text-danger fw-semibold d-flex align-items-center">Suppresion Utilisateur</Modal.Title>
              </Modal.Header>

              <Modal.Body className="text-center py-4">
                <h4 className="fw-semibold mb-3">Suppression définitive</h4>
                <div className="p-3 bg-secondary bg-opacity-25 rounded-3 mb-3 border border-secondary text-start">
                  <div className="small text-light mb-1">Identité utilisateur :</div>
                  <div className="small text-light mb-1 fw-semibold">
                    Retirer <span className="text-warning">{userToDelete?.firstName} {userToDelete?.name}</span> de la base ?
                  </div>
                </div>
              </Modal.Body>

                <Modal.Footer className="d-flex justify-content-end border-secondary border-1 pt-3">
                  <Button variant="outline-light" className="px-3 fw-semibold" onClick={() => setShowDeleteModal(false)}>
                    Non
                  </Button>
                  <Button variant="danger" className="px-3 fw-semibold" onClick={handleConfirmDelete}>
                    Oui, supprimer
                  </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="bottom-end" className="py-5" style={{ position: 'fixed', zIndex: 9999 }}>
              <Toast
                onClose={() => setShowToast(false)}
                show={showToast}
                delay={3000}
                autohide
                bg={toastVariant}
                className="text-white shadow"
              >
                <div className="d-flex align-items-center p-2">
                  <Toast.Body className="m-0 flex-grow-1 d-flex align-items-center">
                    <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>
                      {toastVariant === 'success' ? 'check_circle' : 'error'}
                    </i>
                    <span className="fw-medium">{toastMessage}</span>
                  </Toast.Body>
                  <Button
                    variant="close"
                    variant-white="true"
                    className="me-2 m-auto btn-close-white"
                    onClick={() => setShowToast(false)}
                    aria-label="Fermer"
                  />
                </div>
              </Toast>
            </ToastContainer>
          </Container>
        </div>
      </div>
    </Container>
  );
};

export default AdminDashboard;
